import { extend } from 'lodash-es';
import { AvailableJobGridItem } from 'src/app/main/shared/services/store/scheduler/selectors';
import { SchedulerRole } from './scheduler-roles';
export interface ILicense {
  proSerialNumber?: string;
  serverId: string;
  lastUpdate: string;
  updateCount: number;
  modules: Array<{ name: string; expiry: string; status: string }>;
  machines: Array<{
    unitNum: string;
    name: string;
    expiry: string;
    status: string;
  }>;
  deviceLicensePools: Array<{
    poolId: string;
    poolName: string;
    quantity: number;
    claims: string[];
    expiry: string;
    status: string;
    assignedDeviceIds: string[];
  }>;
  deviceLicenseAssignments: Array<{
    deviceId: string;
    assignmentName: string;
    claims: string[];
    expiry: string;
    status: string;
  }>;
}

export interface IWebhookConfig {
  id: string;
  settingsId: string;
  type: string;
  state: string;
  settings: any;
  messages: string[];
}
export interface IWebhookEvent {
  id: string;
  start: Date;
  successful: boolean;
  request: string;
  response: string;
}

export interface IExternalConnection {
  id: string;
  type: string;
  state: string;
  settings: any;
  messages: string[];
}
export interface ICoilDto {
  id: string;
  coilId: string;
  documentId: string;
  materialCode: string;
  description: string;
  weightStartLbs: number;
  dateIn: Date;
  dateOut: Date;
  importDate: Date;
  vendorName: string;
  vendorCode: string;
  heatNumber: string;
  purchaseOrder: string;
  user1: string;
  user2: string;
  isComplete: boolean;
  lengthStartFt: number;
  lengthRemainingFt: number;
  lengthUsedFt: number;
  lengthNonExemptScrapFt: number;
  lengthExemptScrapFt: number;
  lengthOtherAdjustmentsFt: number;
  currentWeightLbs: number;
  locationId: string;
  gauge: number;
  color: string;
  widthIn: number;
  materialType: string;
}

export interface ICoilDtoLocation extends ICoilDto {
  location: ILocation;
}

export interface IMaterialDoc {
  materialCode: string;
  description: string;
  gauge: number;
  color: number;
  widthIn: number;
  type: string;
  lbsPerFt: number;
  costPerLb: number;
  thicknessIn: number;
}
export interface IMaterialDto extends IMaterialDoc {
  id: string;
  demandFt: number;
  onHandFt: number;
  onHandQty: number;
  balanceFt: number;
}

export interface IJobDetailDto {
  id: string;
  ordId: number;
  job: IJobSummaryDto;
  items: IJobItem[];
  bundles: IJobBundle[];
}

export interface IJobState {
  ordId: number;
  orderCode: string;
  toolingCode: string;
  materialCode: string;
  completeFt: number;
  remainingFt: number;
  totalFt: number; // TODO: Is this not already on JobDetail.Job...do we really need to repeat it?
  startDateTime: Date;
  duration: string;
  completionDate: Date;
}

export interface IJobItem {
  itemId: number;
  bundle: number;
  quantity: number;
  quantityDone: number;
  lengthIn: number;
  patternName: string;
  patternId: string;
  //patternNumber: number; //removing this for now. It might come back.
  option: string;
  sequence: number;
  user1: string;
  user2: string;
  user3: string;
  user4: string;
  user5: string;
  externalItemId: string;
  messageText: string;
  holeCount: number;
  calculateLengthFromHole: boolean;
  bundleCode: string;
  bundleGroup: string;
  doneDate: Date;
  stagger: boolean;
  weightLbs: number;
  pieceMark: string;
  sku: string;
  patternNotDefined: boolean;
  patternSolutionAlert: PatternSolutionAlert;
  partLabelDef: string;
  bundleLabelDef: string;
}

/**
 * Body of POST /api/ordercommand/{ordId}/items. Fields left null are copied from the
 * prototype item on the server; -1 for bundle opens a new bundle after the order's last.
 */
export interface IAddJobItemRequest {
  prototypeItemId: number | null;
  bundle: number;
  quantity: number;
  lengthIn: number;
  patternName: string | null;
  externalItemId: string | null;
  pieceMark: string | null;
  bundleGroup: string | null;
  messageText: string | null;
  sku: string | null;
}

/** A brand-new order. Every order needs at least one item, so the first item rides along. */
export interface ICreateOrderRequest {
  orderCode: string;
  materialCode: string;
  toolingCode: string;
  quantity: number;
  lengthIn: number;
}

/** A coil being added to inventory. It is written to Classic, so an existing coil id is rejected. */
export interface ICreateCoilRequest {
  coilId: string;
  materialCode: string;
  /** Blank takes the material description. */
  description: string | null;
  lengthStartFt: number;
  weightStartLbs: number | null;
  /** Full Location doc id. Blank lands the coil in the default warehouse location. */
  locationId: string | null;
  vendorName: string | null;
  vendorCode: string | null;
  heatNumber: string | null;
  purchaseOrder: string | null;
}

export interface IJobBundle {
  bundleId: number;
  bundle: number;

  user1: string;
  user2: string;
  user3: string;
  user4: string;
  user5: string;
}

export interface IPatternWhereUsedItem {
  ordId: number;
  orderCode: string;
  materialCode: string;
  toolingCode: string;
  customerName: string;
  status: string;
  externalItemId: string;
  quantity: number;
  lengthIn: number;
  sequence: number;
}

export type JobScheduleState =
  | {
      state: 'Scheduled';
      machineNumber: number;
      sequence: number;
      isOnMachine: boolean;
    }
  | { state: 'Unscheduled' | 'Done' };

export type PatternSolutionAlert =
  | 'SolutionFound'
  | 'SolutionNotFound'
  | 'Pending'
  | 'PunchPatternNotFound'
  | 'PunchPatternInvalid'
  | 'NoMachineShapeTable'
  | 'NoPunches'
  | 'SolutionNotNeeded'
  | 'Error'
  | 'ShapeNotOnMachine'
  | 'PatternHasNoShapes'
  | 'FeatureGeometryNotFound'
  | 'SegmentNotOnMachine'
  | 'SegmentCorrectionNotFound'
  | 'ShapePunchingNotLicensed'
  | 'ShapePunchingAdvancedNotLicensed'
  | 'MacroNotFound';

export interface IJobAlertMembers {
  patternSolutionAlert: PatternSolutionAlert;
  patternNotDefined: boolean;
  materialShortageAlert: boolean;
  hold: boolean;
  /** Client-side only: the order's `OrderLate` alert text, when it has one. */
  lateAlert?: string;
}

export interface IJobSummaryDto extends IJobAlertMembers {
  id: string;
  ordId: number;
  orderCode: string;
  toolingCode: string;
  isOnMachine: boolean;
  toolingDescription: string;
  /** @deprecated Use scheduleState instead, or legacyStatus if you really need all those various states COMM provides */
  status: string;
  materialCode: string;
  material: IMaterialDto;
  customerName: string;
  workOrder: string;
  truckNumber: string;
  /** `truckNumber` normalized to its truck route key (trimmed, upper-case; '' when blank). */
  truckKey?: string;
  /** You'll need to call `new Date(x.requiredDate)` or `moment(x.requiredDate)` if you want to use this as an actual `Date`. */
  requiredDate?: string;
  /**
   * The effective due time, from the job's truck route instance. Use this, not `requiredDate`,
   * for lateness. See `specs/truck-schedule.md`.
   */
  dueAt?: string | null;
  dueSource?: DueTimeSource;
  importDate: Date;
  user1: string;
  user2: string;
  user3: string;
  user4: string;
  user5: string;
  customerAddress1: string;
  customerAddress2: string;
  customerCity: string;
  customerState: string;
  customerZip: string;
  customerCountry: string;
  customerInstructions: string;
  stagingBay: string;
  loadingDock: string;
  customerNumber: string;
  customerPhone: string;
  customerPO: string;
  salesOrder: string;
  operatorMessage: string;
  /** You'll need to call `new Date(x.shipDate)` or `moment(x.shipDate)` if you want to use this as an actual `Date`. */
  shipDate?: string;
  legacyStatus: string;
  actionInProcess: boolean;
  completeFt: number;
  remainingFt: number;
  /** Footage × the material's lbs/ft; 0 when the material is unknown. */
  totalLbs: number;
  /** Footage × the material's lbs/ft; 0 when the material is unknown. */
  remainingLbs: number;
  totalFt: number;
  quantity: number;
  quantityDone: number;
  startDateTime: Date;
  duration: string;
  completionDate: Date;
  scheduleState: JobScheduleState;
  /** @deprecated Use scheduleState instead */
  sequence: number;
  /** @deprecated Use scheduleState instead */
  machineNumber: number;

  materialColor: string;
  materialGauge: number;
  materialWidthIn: number;
  materialDescription: string;
  longestLengthIn: number;
  shortestLengthIn: number;
  materialShortageAlert: boolean;
  bundleCount: number;
  isDeleted: boolean;
}
export interface IAvailableJob {
  id: string;
  expectedRuntime: number;
  warningDueDate: boolean;
  pastDueDate: Date;
  machineNumber: number;
  ordId: number;

  toolingCode: string;
  materialCode: string;
  /** You'll need to call `new Date(x.requiredDate)` or `moment(x.requiredDate)` if you want to use this as an actual `Date`. */
  requiredDate?: string;
  orderCode: string;
  customerName: string;
  totalFt: number;
  remainingFt: number;
  /** Total pieces to produce for this job (sum of line-item quantities). */
  totalPieces: number;
  /** Remaining pieces to produce; for an unscheduled job this is the total piece count. */
  remainingPieces: number;
  truckNumber: string;
  stagingBay: string;
  loadingDock: string;
  materialColor: string;
  materialGauge: number;
  materialWidthIn: number;
  materialDescription: string;
  toolingDescription: string;
  longestLengthIn: number;
  salesOrder: string;
  user1: string;
  user2: string;
  user3: string;
  user4: string;
  user5: string;
  workOrder: string;
  importDate: Date;
  /** You'll need to call `new Date(x.shipDate)` or `moment(x.shipDate)` if you want to use this as an actual `Date`. */
  shipDate?: string;
  hold: boolean;
  /**
   * Client-side only: the user just unscheduled this job and the server hasn't confirmed it yet
   * (see store/scheduler/schedule-overlay.ts).
   */
  provisional?: boolean;
}

export type OrderStatus =
  | 'Unscheduled'
  | 'Sequenced'
  | 'Scheduled'
  | 'Sending'
  | 'Machine'
  | 'Return'
  | 'Recall'
  | 'Done'
  | 'Unknown'
  | 'Error'
  | 'ToSequence'
  | 'ToUnschedule'
  | 'ToSend'
  | 'ToRecall'
  | 'ToHold'
  | 'ToUnhold';

export type ScheduledJobGridItem = IScheduleItem &
  IJobSummaryDto & {
    isSelected: boolean;
    requiredDateDisplay: string;
    importDateDisplay: string;
    shipDateDisplay: string;
    mainIndex?: number;
    completePct: number;
    /** Remaining pieces to produce (quantity - quantityDone). */
    remainingPieces: number;
    // remainingRuntime: string;
  };

export type RangeValue = {
  start: string | number;
  end: string | number;
};
export type ScheduledJobColumnFieldName = keyof ScheduledJobGridItem | 'remainingRuntime';

export interface IScheduledJobColumn {
  fieldName: ScheduledJobColumnFieldName;
  name: string;
  isChecked: boolean;
  /** Which of the three scheduler roles this column belongs to. */
  role: SchedulerRole;
  displayName: string;
  units: string;
  width?: number;
  summarizer(
    items: ScheduledJobGridItem[],
    column: { fieldName: ScheduledJobColumnFieldName },
  ): string | RangeValue;
}
export interface IAvailableJobColumn {
  summarizer(jobs: AvailableJobGridItem[], column: IAvailableJobColumn): string | RangeValue;
  fieldName: keyof AvailableJobGridItem;
  width?: number;
  name: string;
  isChecked: boolean;
  /** Which of the three scheduler roles this column belongs to. */
  role: SchedulerRole;
  displayName: string;
  units: string;
}
export interface IUserColumnChoice {
  field: string;
  isChecked: boolean;
  width?: number;
}
export interface IScheduledDowntimeDto {
  id: string;
  documentID: string;
  machines: number[];
  title: string;
  occurs: string;
  timeOfDay: string;
  duration: string;
  activityType: string;
  startDate: Date;
  occurenceCount?: number;
  daysOfWeek?: string[];
  endDate: Date;
  endRepeat: string;
  everyCount: number;
  monthValue: string;
  selectedDate?: number[];
  dayOfMonth: string;
  weekDayOfMonth: string;
  machinesText: string;
  occursText: string;
}
export interface IDashboardMachine {
  machineNumber: number;
  machine: IMachine;
  state: IMachineStateDto;
  stats: IRollformingStatistics;
  statsHistory: IStatisticsHistory;
  scheduleSummary: IScheduleSummary;
  scheduleEstimate: IScheduleEstimate;
  metricSettings: IMachineMetricSettings;
  metrics: IMachineMetrics;
}

export interface IMachineStateDto {
  id: string;
  machineNumber: number;
  lastRunStateChange: Date;
  runState: string;
  // "machineName": "Multi-profile Line",//not needed
  // "eventDateTime": "2016-02-16T13:54:37-06:00",
  /** The OrdId of the order on the machine; -1 or null when there is none. */
  orderId?: number | null;
  orderCode: string;
  materialCode: string;
  toolingCode: string;
  bundleNumber: number;
  currentItemAddress: string;
  currentItemLengthIn: number;
  currentItemExternalId: string;
  currentItemQty: number;
  currentItemQtyDone: number;
  currentItmId: number;
  currentCustomerName: string;
  currentWorkOrder: string;
  currentCoilId: string;
  currentCoilMaterialCode: string;
  currentCoilRemainingFeet: number;
  employeeNumber: string;
  employeeName: string;
  currentShiftCode: string;
  lockOutEnabled: boolean; // todo: verify if this is sent and what it might do
  isHoleCount: boolean;
  lockout: 'Locked' | 'Unlocked' | 'Unknown';
  canSetLockout: boolean;
  connectionStatus?: 'Unknown' | 'Offline' | 'Online';
  modalWindowOpen: boolean;
  // "serverReceivedDate": "2017-02-24T00:06:06.0382563-06:00"
}

export interface IScheduleSummary {
  id: string;
  // "documentID": "MachineScheduleSummary/1",
  machineNumber: number;
  currentOrderId: number;
  currentOrderCode: string;
  currentMaterialCode: string;
  currentToolingCode: string;
  currentOrderTotalFeet: number;
  currentOrderRemainingFeet: number;
  currentOrderPercentComplete: number;
  currentMaterialRemainingFeet: number;
  currentToolingRemainingFeet: number;
  currentBundleNumber: number;
  currentBundleQuantity: number;
  currentBundleQuantityDone: number;
  lastBundleNumber: number;
  currentLength: number;
  currentItemExternalId: string;
  currentItemQty: number;
  currentItemQtyDone: number;
  currentCustomerName: string;
  currentCoilId: string;
  currentCoilMaterialCode: string;
  currentCoilRemainingFeet: number;
  nextOrderId: number;
  nextOrderCode: string;
  nextMaterialCode: string;
  nextToolingCode: string;
  nextOrderFeet: number;
  nextMaterialFeet: number;
  nextToolingFeet: number;
  nextOrderCustomerName: string;
  nextWorkOrder: string;
  scheduledFeetTotal: number;
  scheduledJobsCount: number;
  availableFeetTotal: number;
  availableJobsCount: number;
  atMachineFeetTotal: number;
  scheduleCompletionDate: Date;
  currentToolingCompletionDate: Date;
  currentToolingRuntimeMs: number;
  currentMaterialCompletionDate: Date;
  currentMaterialRuntimeMs: number;
  currentOrderCompletionDate: Date;
  currentOrderRuntimeMs: number;
  nextOrderRuntimeMs: number;
  nextToolingDurationMs: number;
  nextToolingRuntimeMs: number;
  nextMaterialDurationMs: number;
  nextMaterialRuntimeMs: number;
  nextOrderDurationMs: number;
  previousCurrentNext: IRecentAndUpcoming;
  minutesBehind: {
    status: 'EmptySchedule' | 'NoHistory' | 'Late' | 'OnTime';
    behind?: string;
    behindMs?: number;
    behindFt?: number;
  };
}

/** A subset of what is on IJobSummaryDto */
export interface IJobBits {
  ordId: number;
  orderCode: string;
  materialCode: string;
  toolingCode: string;
  completeFt: string;
  quantity: string;
  quantityDone: string;
  requiredDate: string;
  completionDate: string;
  customerName: string;
  patternNotDefined: boolean;
  hold: boolean;
  materialShortageAlert: boolean;
  totalFt: number;
  remainingFt: number;
  // The payload is the full job summary (JobSummaryDtoManager.Dto), so these
  // material attributes are present at runtime and usable as color-rule
  // source fields. See specs/color-rules.md.
  materialGauge: number;
  materialWidthIn: number;
  materialColor: string;
  materialDescription: string;
}
export interface IRecentAndUpcoming {
  future: IJobBits[];
  current: IJobBits;
  past: IJobBits[];
}

export interface IScheduleEntry {
  id: string;
  activityType: string;
  startDateTime: Date;
  //TimeSpan Duration { get; set; }
  endDateTime: Date;
  durationMs: number;
  machineNumber: number;
  orderCode: string;
  materialCode: string;
  toolingCode: string;
  ordId: number;
  remainingFt: number;
  totalFt: number;
  customerName: string;
  jobId: string;
  title: string;
  state: string;
  downtimeId: string;
  showLeftLine: boolean;
  showRightLine: boolean;
  changeoverDetails?: IChangeoverDetails;
  bundleSummary?: IBundleSummary;
  /**
   * True feet the block produces, from the per-item lengths. Prefer this over `remainingFt`,
   * which is the whole job's remaining stamped on every block of the job (and is in
   * inches-times-pieces, not feet). Zero on non-production blocks.
   */
  productionFt?: number;
  /** Set on production blocks — the job's required date, if it has one. */
  requiredDate?: string | null;
  /** True when this block belongs to a job that is already at the machine. */
  isOnMachine?: boolean;
  sequenceNum?: number | null;
  materialShortageAlert?: boolean;
  /** Block warning names, e.g. material shortage. Rendered as CSS classes by the timeline. */
  warnings?: string[];
}

export interface IBundleSummary {
  bundleNumber: number;
  pieceCount: number;
  lengthIn: number;
  weightLbs: number;
}

export interface IChangeoverDetails {
  matchedRules: IChangeoverRuleMatch[];
  totalTimeSec: number; // Total time in seconds
  usedPlanValues: boolean;
  isLegacyCalculation: boolean;
  fromMaterialCode: string;
  toMaterialCode: string;
  fromToolingCode: string;
  toToolingCode: string;
}

export interface IChangeoverRuleMatch {
  attribute: string;
  category: string;
  fromValue: string;
  toValue: string;
  timeSec: number; // Time in seconds
}

export interface IScheduleEstimate {
  id: string;
  documentId: string;
  machineNumber: number;
  scheduleBlocks: IScheduleEntry[];
  asOf: Date;
}

/**
 * Quantified before/after numbers for an auto-scheduling proposal. See
 * `specs/auto-scheduling.md`. Null on the response when no improvement was found.
 */
export interface IScheduleOptimizeDeltas {
  changeoverMinutesBefore: number;
  changeoverMinutesAfter: number;
  changeoverMinutesSaved: number;
  onTimeBefore: number;
  onTimeAfter: number;
  totalJobs: number;
  lateJobsBefore: number;
  lateJobsAfter: number;
  coilChangesBefore: number;
  coilChangesAfter: number;
}

/**
 * Response of `POST /_api/schedule/optimize`. Nothing is persisted server-side — the
 * proposal lives in this response and the client holds it until Apply or Discard.
 */
export interface IScheduleOptimizeResult {
  improved: boolean;
  /** Human-readable reason when `improved` is false, e.g. "No improvement found". */
  message: string | null;
  baseline: IScheduleEstimate;
  proposed: IScheduleEstimate | null;
  /** OrdIds in the current sequence — echoed back on apply as the stale-proposal guard. */
  baselineOrder: number[];
  /** OrdIds in the proposed sequence; empty when `improved` is false. */
  proposedOrder: number[];
  deltas: IScheduleOptimizeDeltas | null;
  /**
   * Resolved deadline per OrdId (requiredDate + plant cutoff; end of day when unset) — exactly
   * what the optimizer scored lateness against. Jobs without a required date are absent.
   */
  dueTimes: { [ordId: number]: string };
}

export interface IHistVal {
  shiftCode: string;
  value: number;
}

export interface IStatisticsHistory {
  id: string;
  machineNumber: number;
  oEEHistory: IHistVal[];
  targetPctHistory: IHistVal[];
  scrapPctHistory: IHistVal[];
  goodFeetHistory: IHistVal[];
  runPctHistory: IHistVal[];
}

export interface IRollformingStatistics {
  id: string;
  documentID: string;
  machineNumber: number;
  start: Date;
  end: Date;
  startShiftCode: string;
  endShiftCode: string;
  recordCount: number;

  toolingChangeCount: number;
  coilChangeCount: number;
  materialChangeCount: number;
  goodPieceCount: number;
  scrapPieceCount: number;

  goodFeet: number;
  goodPct: number;
  scrapFeet: number;
  scrapPct: number;
  reclaimedScrapFeet: number;
  reclaimedScrapPct: number;
  scrapPareto: IAggregate[];

  totalMinutes: number;
  runMinutes: number;
  runPct: number;

  totalDelayMinutes: number;

  totalDelayPct: number;
  nonExemptMinutes: number;
  nonExemptPct: number;
  exemptMinutes: number;
  exemptPct: number;

  offlineMinutes: number;
  offlinePct: number;
  totalNonExemptMinutes: number;
  downtimePareto: IAggregate[];

  avgFPM: number;
  avgThroughput: number;
  oee: number;
  targetPct: number;

  actualGoodFeet: number;
  expectedGoodFeet: number;
  targetFPM: number;
  targetGoodFeet: number;
  totalFeet: number;
  totalOperationMinutes: number;
  statusEventTime: Date;
  productionEventTime: Date;
  changeoverMinutes: any;
  breakdownMinutes: any;
}

export interface IAggregate {
  typeId: number;
  name: string;
  instanceCount: number;
  value: number;
  minValue: number;
  maxValue: number;
  codeExempt: string;
  codeResponsibility: number;
}

export interface IMachineHourStatistics {
  machineNumber: number;
  date: Date;
  hour: number;
  hourStart: Date;
  recordCount: number;
  shiftCode: string;

  // Change counts
  toolingChangeCount: number;
  coilChangeCount: number;
  materialChangeCount: number;
  totalCutCount: number;
  goodPieceCount: number;
  scrapPieceCount: number;

  // Material (feet)
  goodFeet: number;
  goodPct: number;
  scrapFeet: number;
  scrapPct: number;
  reclaimedScrapFeet: number;
  reclaimedScrapPct: number;
  totalFeet: number;
  scrapPareto: IAggregate[];

  // Time (minutes)
  totalMinutes: number;
  runMinutes: number;
  runPct: number;
  nonExemptMinutes: number;
  nonExemptPct: number;
  exemptMinutes: number;
  exemptPct: number;
  unreportedMinutes: number; // Populated by API for current shift unreported time (machine halted)
  offlineMinutes: number;
  offlinePct: number;
  totalDelayMinutes: number;
  totalDelayPct: number;
  totalNonExemptMinutes: number;
  totalOperationMinutes: number;
  downtimePareto: IAggregate[];

  // Performance metrics
  avgFPM: number;
  avgThroughput: number;
  throughput: number;
  potentialFt: number;
  yieldPotential: number;
  speedPct: number;
  yieldPct: number;
  availability: number;
  oee: number;
  targetPct: number;

  // Calculation helpers
  runMinutesXTargetFPM: number;
  actualGoodFeet: number;

  // Per-hour plan-vs-actual measurement, projected from RollformingShift.HourlyBehind.
  // 0 when the corresponding hour bucket doesn't exist (shift hasn't reached that hour or
  // pre-feature data).
  planLengthFt: number;
  actualLengthFt: number;
  targetLengthFt: number;
  planRunMinutes: number;
  actualRunMinutes: number;
}

export interface IShiftChoice {
  index: number;
  shiftCode: string;
  shiftDate: Date;
  shift: number;
}

// IMachineMetricSettings contains customizations per machine
export interface IMachineMetricSettings {
  id: number;
  documentId: string;
  machineNumber: number;
  settings: IMetricConfig[];
}

// IMetricConfig contains the settings for a single metric
export interface IMetricConfig {
  metricId: string;
  targetValue: number;
  okRangeStart: number;
  okRangeEnd: number;
  maxValue: number;
  minValue: number;
  showInMini: boolean;
  showInLarge: boolean;
}

// IMetricDefinition contains the definition of a metric
export interface IMetricDefinition {
  metricId: string;
  displayTitle: string;
  collection: string;
  primaryDataKey: string;
  primaryUnits: string;
  secondaryDataKey: string;
  secondaryUnits: string;
  historyKey: string;
  lowerIsBetter: boolean;
  targetValue: number;
  minValue: number;
  maxValue: number;
  okRangeStart: number;
  okRangeEnd: number;
  nameToolTip: string;
  primaryToolTip: string;
  secondaryToolTip: string;
  historyToolTip: string;
  showInMini: boolean;
  showInLarge: boolean;
  showCharts: boolean;
}

// IMachineMetrics is the merged list of settings and definitions
export interface IMachineMetrics {
  machineNumber: number;
  settings: IMetricConfigWithDef[];
}

export interface IMetricConfigWithDef extends IMetricConfig {
  def: IMetricDefinition;
}

export interface IMachineSelection {
  type: string; // 'xl' | 'device';
  xlId: number;
  deviceId: string;
}

export interface IRunblockData {
  runBlocks: IRunblockItem[];
  machineEvents: IRunblockMachineEvent[];
  scheduledDowntimes: IRunblockScheduledDowntime[];
}
export interface IRunblockItem {
  machineStatus: string;
  ordId: number;
  codeValue: number;
  startTime: string;
  endTime: string;
  durationMinutes: number;
  quantity: number;
  producedLengthIn: number;
  feetPerMinute: number;
  orderCode: string;
  materialCode: string;
  toolingCode: string;
  codeDescription: string;
  codeExempt: string;
  employeeName: string;
  employeeNumber: string;
  customerName: string;
}

export interface IRunblockMachineEvent {
  recordDate: Date;
  type: string;
  description: string;
  relatedId: string;
}

export interface IRunblockScheduledDowntime {
  activityType: string;
  downtimeId: string;
  durationMinutes: number;
  machineNumbers: number[];
  endTime: string;
  startTime: string;
  state: string;
  title: string;
}

export interface IMachineTooling {
  id: string;
  machineId: string;
  toolingId: string;
}

export type LockdownCodeValue = 'Always' | 'WithKey' | 'Never';
export type LockdownCode = { label: string; value: LockdownCodeValue };
type LockdownCodeModel = LockdownCode[];
type EclipseEnforcedSetup = {
  value: number;
  isEnabled: boolean;
  isLocked: boolean;
  label: string;
  help: string;
  available: boolean;
};

export interface IMachine {
  id: number;
  machineNumber: number;
  description: string;
  isActive: boolean;
  isLicensed: boolean;
  ipAddress: string;
  commName: string;
  uartVersion: number;
  // You probably don't want to access this directly.
  // Go look at `machineHasClaim` function.
  claims: string[];
  autoPushEnabled: boolean;
  autoPushMinFt: number;
  orderLockdownCode: string;
  orderLockdownModel: LockdownCodeModel;
  patternLockdownCode: string;
  patternLockdownModel: LockdownCodeModel;
  eclipseEnforcedSetups: {
    haltDelayMinimum: EclipseEnforcedSetup;
    useScrapCodes: EclipseEnforcedSetup;
    manualShearScrapLengthIn: EclipseEnforcedSetup;
    enforceEclipseCoilValidation: EclipseEnforcedSetup;
    enforceBundlingRules: EclipseEnforcedSetup;
    useCoilInventory: EclipseEnforcedSetup;
    allowCoilOverride: EclipseEnforcedSetup;
    autoRequestOrderFootage: EclipseEnforcedSetup;
    displayBundleIdPrompts: EclipseEnforcedSetup;
    autoDeleteDoneOrdersAfter: EclipseEnforcedSetup;
    showUserDataProgram: EclipseEnforcedSetup;
    showUserDataStatus: EclipseEnforcedSetup;
    staggerPanelField: EclipseEnforcedSetup;
    setDoneItemsToReady: EclipseEnforcedSetup;
    enableScrapCoilDuringUnload: EclipseEnforcedSetup;
    validate900Bundles: EclipseEnforcedSetup;
    showPreThreadUpScrapWindow: EclipseEnforcedSetup;
  };
  isHoleCount: boolean;
  eclipseEnforcedSetupsWarning?: string;
  lockdownWarning?: string;
  softwareModel?: string;
  softwareVersion?: string;
  serialNumber?: string;
  switchCode?: string;
  warehouseTaskConfig?: IWarehouseTaskConfiguration;
  autoLock: boolean;
  unlockUntil?: string;
}

export interface IMachineUserField {
  fieldGroup: 'Order' | 'Item';
  fieldNumber: number;
  caption: string;
  fieldDefinition: string;
  fieldDefinitionDisplay: string;
  fieldDefinitionFriendly: string;
  isActive: boolean;
}

export interface IMachineUserFields {
  machineNumber: number;
  fields: IMachineUserField[];
  changeId: number;
}

export interface IMachineUserFieldPatch {
  fieldGroup: 'Order' | 'Item';
  fieldNumber: number;
  isActive?: boolean;
  caption?: string;
  fieldDefinition?: string;
}

export interface IUserFieldMacro {
  displayName: string;
  resolvedDisplayName: string | null;
  macroCode: string;
  defaultPosition: number;
  defaultLength: number;
  formatCategory: string | null;
  defaultFormatCode: string | null;
}

export interface IFormatOption {
  code: string;
  displayName: string;
}

export interface IWarehouseTaskConfiguration {
  enableReturnTasks: boolean;
  completePreviousTasks: boolean;
  defaultReturnLocationId?: string;
  maxEventAgeHours: number;
  processEventsAfter?: Date | string;
  lastProcessedEventId?: string;
}

export interface ITool {
  tool: number;
  press: number;
  gag: number;
  axis: number;
  offsetIn: number;
  yOffsetIn: number;
  name: string;
  kerfAdjustIn: number;
}

export interface IMachineTools {
  documentID: string;
  machineNumber: number;
  date: Date;
  softwareModel: string;
  softwareVersion: string;
  controllerListId: number;
  libraryName: string;
  libraryId: number;
  uartVersion: number;
  tools: ITool[];
}

export interface IMachineAxes {
  machineNumber: number;
  date: Date;
  softwareModel: string;
  softwareVersion: string;
  libraryName: string;
  libraryId: number;
  uartVersion: number;
  axes: IAxis[];
}

export interface IAxis {
  axisNumber: number;
  type: string;
  typeVersion: number;
  driver: string;
  driverVersion: number;
  axisName: string;
  networkId: number;
  typeDriver: string;
  typeDriver2: string;
  opposingAxisId: number;
  crashableSurfaceIn: number;
  minPositionIn: number;
  maxPositionIn: number;
}

export interface IMachineSetup {
  changedParameters: number;
  date: string;
  id: string;
  isSavedLibrary: boolean;
  machineNumber: number;
  name: string;
  softwareModel: string;
  softwareVersion: string;
  switchCode: string;
  totalParameters: number;
  uartVersion: number;
  parameters?: IMachineSetupParam[];
}

export interface IMachineSetupParam {
  flags: string;
  hasChanged: boolean;
  paramGroupId: number;
  paramId: string;
  setupName: string;
  setupValue: string;
}

export interface IMachineSetupHistoryItem {
  date: string;
  setupId: string;
  paramId: string;
  setupName: string;
  setupValue: string;
  flags: string;
  hasChanged: boolean;
  isFromSelectedSetup: boolean;
}

export interface IMachineCompareSetupValue {
  hasChanged: boolean;
  setupValue: string;
  isSavedLibrary?: boolean;
}

export interface IMachineCompareItem {
  hasChanged: boolean;
  paramGroupId: number;
  paramId: string;
  setupName: string;
  setupValues: IMachineCompareSetupValue[];
}

export interface IMachineSetupCompare {
  comparison: IMachineCompareItem[];
  setupIds: string[];
}

/// One entry in a machine's config history. Setup and tool config documents are append-only —
/// one per config upload the controller reported — and `id` is the date code used to fetch that
/// snapshot's contents. Shared by the setups and tools config snapshot lists.
export interface IMachineConfigSnapshot {
  id: string;
  machineNumber: number;
  date: string;
  softwareModel: string;
  softwareVersion: string;
  uartVersion: number;
  switchCode?: string;
  libraryName: string;
  libraryId: number;
}

export interface IMachineSetupsConfigs {
  machineNumber: number;
  date: string;
  softwareModel: string;
  softwareVersion: string;
  uartVersion: number;
  switchCode: string;
  libraryName: string;
  libraryId: number;
  configs: IMachineSetupsConfig[];
}

export interface IMachineSetupsConfig {
  configId: number;
  configName: string;
  parameters: IMachineSetupsConfigParam[];
}

export interface IMachineSetupsConfigParam {
  setupName: string;
  setupValue: string;
}

export interface IMachineToolingConfigDto {
  toolingCode: string;
  hasCtrlPcdeRow: boolean;
  hasToolingDef: boolean;
  setupsConfigId: number;
  toolsConfigId: number;
  positions: number;
  action: string;
  isCompleted: boolean;
  status: string;
  isFound: boolean;
}

export interface IToolingConfigChange {
  toolingCode: string;
  setupsConfigId: number;
  toolsConfigId: number;
  op: 'Add' | 'Delete';
}

export interface IMachineToolsConfigs {
  machineNumber: number;
  date: string;
  softwareModel: string;
  softwareVersion: string;
  uartVersion: number;
  libraryName: string;
  libraryId: number;
  toolConfigs: IMachineToolConfig[];
}

export interface IMachineToolConfig {
  configId: number;
  configName: string;
  tools: ITool[];
}

/// Part printing configuration (not to be confused with bundle and coil tag printing)
export interface IMachinePrintConfiguration {
  enablePartPrintingShapeInjection: boolean;
  lengthTable: { pattern: IPunchPattern; upToLength: number }[];
  defaultPattern: IPunchPattern;
}

export interface IDevice {
  id: string;
  name: string;
  deviceId: string;
  accepted?: boolean;
}

export interface IDeviceConfig extends IDevice {
  topicRoot?: string;
  description?: string;
  processVariablesToTrack?: string[];
  countingMode?: string;
  inputSource?: string;
  haltDelayMinimumMinutes?: number;
  allowQuickStart?: boolean;
  requirePartId?: boolean;
  requireJobReference?: boolean;
  maxPiecesAtATime?: number;
  staleTimeoutSeconds?: number;
  assignedPoolId?: string;
  assignedPoolName?: string;
  assignedClaims?: string[];
  licenseExpiry?: string;
  licenseAssignedAt?: string;
  licenseAssignedBy?: string;
  deviceType?: string;
  isPortalAssigned?: boolean;
}

export interface IPortalDeviceLicenseAssignment {
  deviceId: string;
  assignmentName: string;
  claims: string[];
  expiry: string;
  isExpired: boolean;
  isApplied: boolean;
}

export interface IDeviceLicensePool {
  poolId: string;
  poolName: string;
  quantity: number;
  claims: string[];
  expiry: string;
  assignedCount: number;
  availableCount: number;
  isExpired: boolean;
  isSoftDeleted: boolean;
  canAssign: boolean;
}

export interface IDashboardDevice extends IDevice {
  state: IDeviceState;
  shiftStats: any;
  shiftStatsWithUnreported?: IDeviceShiftStatistics; // Combined shift stats + unreported counts
  metricConfigs: IDeviceMetricConfigWithDef[];
  metrics: IDeviceMetrics;
  metricModels: any;
}

export interface IDeviceState {
  id: string;
  deviceId: string;
  runState: string;
  machinePower: boolean;
  faulted: boolean;
  badParts: number;
  goodParts: number;
  cycles: number;
  asOf: string;
  milliSecSinceBoot: number;
  override: boolean;
  operator: string;
  currentPartId: string;
  currentPartName: string;
  currentGeometry: string;
  currentPartEdgeId: string;
  isTimingOut: boolean;
  unreportedCycles?: number;
  unreportedGoodParts?: number;
  unreportedBadParts?: number;
  currentShiftCode?: string; // yyyyMMdd format - the shift that unreported counts belong to
  // Work session fields
  hasActiveWorkSession?: boolean;
  jobReference?: string;
  targetQuantity?: number;
  sessionGoodParts?: number;
  sessionCycles?: number;
  inputSource?: string;
  piecesAtATime?: number;
}
export interface IDeviceStateIndicatorModel extends IDeviceState {
  lastRunStateChange: Date;
  isNoPower: boolean;
  isManualMode: boolean;
  isOffline: boolean;
  isFaulted?: boolean;
  runState: string;
}
export interface IDeviceMetrics {
  id: string;
  deviceId: string;
  oee: number;
  speedPpm: number;
  scrapCount: number;
  cyclesPm: number;
}
export interface IDeviceShiftStatistics extends IDeviceShiftStatisticsMetrics {
  id: string;
  deviceId: string;
  byHour: (IDeviceShiftStatisticsMetrics & {
    hour: number;
  })[];
  downtimePareto?: IDowntimeParetoItem[];
}

export interface IDowntimeParetoItem {
  typeId: number;
  name: string;
  instanceCount: number;
  durationMinutes: number;
  codeExempt: string;
}

export interface IDeviceShiftStatisticsMetrics {
  shiftCode: string;
  totalMinutes: number;
  totalOperations: number;
  totalParts: number;
  runMinutes: number;
  nonExemptMinutes: number;
  exemptMinutes: number;
  avgOpsPerHrTotal: number;
  avgOpsPerHrRunning: number;
}

/** ITimeSeriesEntry represents a single data point in a time series */
export interface ITimeSeriesEntry {
  timestamp: string;
  value: number;
  tag?: string;
}

/** IDeviceTimeSeries contains time series data for a specific metric */
export interface IDeviceTimeSeries {
  deviceId: string;
  shiftCode: string;
  seriesName: string;
  entries: ITimeSeriesEntry[];
}

/** IDeviceAllTimeSeries contains all time series data for a shift */
export interface IDeviceAllTimeSeries {
  deviceId: string;
  shiftCode: string;
  series: { [key: string]: ITimeSeriesEntry[] };
}

/** ITimeSeriesAggregatedEntry represents an aggregated time series entry */
export interface ITimeSeriesAggregatedEntry {
  from: string;
  to: string;
  value: number;
  count: number;
}

/** IDeviceAllTimeSeriesAggregated contains aggregated time series data for a shift */
export interface IDeviceAllTimeSeriesAggregated {
  deviceId: string;
  shiftCode: string;
  groupByMinutes: number;
  aggregation: string;
  series: { [key: string]: ITimeSeriesAggregatedEntry[] };
}

/** IMetricConfig contains the settings for a single metric */
export interface IDeviceMetricConfig {
  metricId: string;
  // targetValue: number;
  // okRangeStart: number;
  // okRangeEnd: number;
  // maxValue: number;
  // minValue: number;
  showInMini: boolean;
  showInLarge: boolean;
}

/** IMetricDefinition contains the definition of a metric */
export interface IDeviceMetricDefinition {
  // metricId: string;
  displayTitle: string;
  collection: string;
  primaryDataKey: string;
  primaryUnits: string;
  secondaryDataKey: string;
  secondaryUnits: string;
  // historyKey: string;
  // lowerIsBetter: boolean;
  // targetValue: number;
  // minValue: number;
  // maxValue: number;
  // okRangeStart: number;
  // okRangeEnd: number;
  // nameToolTip: string;
  primaryToolTip: string;
  secondaryToolTip: string;
  // historyToolTip: string;
  // showInMini: boolean;
  // showInLarge: boolean;
  showCharts: boolean;
}

export interface IDeviceMetricConfigWithDef extends IDeviceMetricConfig {
  def: IDeviceMetricDefinition;
}

export interface IDeviceProducedGoods {
  deviceId: string;
  partId: string;
  partName: string;
  jobReference: string;
  edgeId: string;
  geometry: string;
  cyclesPerPart: number;
  totalParts: number;
  totalOperations: number;
  totalMinutes: number;
  runMinutes: number;
  cyclesPerHour?: number;
  operator: string;
  startDate: string; //datetime
  endDate: string; //datetime
  date?: string; //date only (used in produced goods report)
}

export interface ISchedule {
  id: string;
  machineNumber: number;
  sequence: (IScheduleItem & { job: IJobSummaryDto })[];
}
export interface IScheduleItem {
  id: string;
  isOnMachine: boolean;
  machineNumber: number;
  ordId: number;
  pastDueDate: boolean;
  remainginRuntime: string;
  sequenceNum: number;
  warningDueDate: boolean;
  warningMaterial: boolean;
  // These members are not sent in the json
  pastDueText: string;
  actionInProgress: boolean;
  warningText: string;
  /**
   * Client-side only: the user just moved this job here and the server hasn't confirmed it yet
   * (see store/scheduler/schedule-overlay.ts). Not estimated, so it has no times.
   */
  provisional?: boolean;
}
export interface IJobs {
  id: string;
  orderId: string;
  requiredDate: Date;
  importDate: Date;
  totalFt: number;
  hold: Boolean;
  customerName: string;
  workOrder: string;
  truckNumber: string;
  stagingBay: string;
  loadingDock: string;
  customerAddress1: string;
  customerAddress2: string;
  customerCity: string;
  customerState: string;
  customerZip: string;
  customerCountry: string;
  customerInstructions: string;
  toolingId: string;
  coilTypeId: string;
  JobStateId: string;
}

export interface ITooling {
  id: string;
  name: string;
  description: string;
}

/** As returned from `/_api/tooling`.
 */
export interface ToolingHeader {
  machines: {
    machineNumber: number;
    description: string;
  }[];
  id: string;
  description: string;
}
/** As returned from `/_api/tooling/{toolingCode}`.
 */
export interface ToolingDef {
  id: string;
  toolingCode: string;
  needNormalizing: {
    descriptions: boolean;
    pCodeGroups: boolean;
    finWidths: boolean;
    legHeights: boolean;
    legHeights2: boolean;
  };
  description: string;
  pCodeGroup: string;
  legHeight: string;
  legHeight2: string;
  profile: string;
  finWidth: number;
  machines: {
    calcLength: number;
    description: string;
    finWidth: number;
    holeCount: number;
    holeSpace: number;
    legHeight: number;
    legHeight2: number;
    loadDock: string;
    machineName: string;
    machineNumber: number;
    pcodeGroup: string;
    preferred: boolean;
    stageBay: string;
    id: string;
  }[];
  hasUnassignedMachineTool?: boolean;
  /** True when at least one real machine (not the unassigned placeholder) is assigned. */
  hasAssignedMachines?: boolean;
  /** True when any job references this tooling code; blocks deletion of the tooling. */
  hasJobDetails?: boolean;
}

export interface PatternDef {
  id: string;
  patternName: string;
  defaultLength: number;
  option: string;
  importDate: Date;
  isPermanent: boolean;
  hash: string;
  lastUsedDate: Date;
  patternNumber: number;
  isMacro: boolean;
  /** Server guarantees these are indeed sequential, starting at 1. */
  punches: PatternDefPunch[];
  changeId: number;
  machinePatterns: MachinePattern[];
}

export interface PatternDefPunch {
  /**
   * 'ShapeMacro' is a call to a macro of shape punches. The editor offers one "Macro" type; the
   * server stores the kind of the macro named (specs/shape-macros.md, SM-D9).
   */
  idType: 'Macro' | 'Tool' | 'Shape' | 'ShapeMacro';
  macroPatternName: string;
  shapeCode: string;
  punchId?: number;
  sequence: number;
  toolId: number;
  xOffset: number;
  xReference: string;
  yOffset: number;
  yReference: string;
  /** Optional profile segment name for the Y position (shape punches only); blank = machine-referenced. */
  ySegment?: string;
}

export interface PatternDefPunchError {
  field: string;
  input: string;
  error: string;
}

/**
 * What a macro's rows make it (specs/shape-macros.md). Tool macros go to the controller;
 * relocatable and placed shape macros are exploded by Pro before the solve.
 */
export type MacroKind = 'Tool' | 'Relocatable' | 'Placed' | 'Invalid';

export interface AvailableMacro {
  id: string;
  macroName: string;
  kind: MacroKind;
}

/** A pattern that calls a macro ("Used by patterns" on the macro's Where Used tab). */
export interface IPatternMacroCaller {
  id: string;
  patternName: string;
  isPermanent: boolean;
  callCount: number;
}

export interface ReferenceColumnsDef {
  value: string;
  text: string;
  resultingRefValue?: string[];
}

export interface MachinePattern {
  id: string; // Composite key: "MachinePattern/{machineNumber}/{pattern}"
  machineNumber: number;
  pattern: number;
  sourcePatternName: string;
  sourcePatternId: string;
  sourceSolutionId: string;
  status: string;
  action: string; // Action command for controller ('D' = delete)
  isCompleted: boolean; // Whether the controller has completed the action
  operations: MachinePatternOperation[];
}

export interface MachinePatternOperation {
  idType: string;
  tool: number;
  sequence: number;
  xOffset: number;
  xReference: string;
  yOffset: number;
  yReference: string;
}

export interface ICoilType {
  id: string;
  name: string;
  widthIn: number;
  gauge: number;
  color: string;
  lbsPerFt: number;
  materialType: string;
  coilTypeCode?: string;
}

export interface ICoil {
  id: string;
  serialNumber: string;
  vendor: string;
  startingLength: number;
  storageLocation: string;
  coilTypeId: string;
}

export interface IJobStateDto {
  id: string;
  completionDate: Date;
  remaingRuntime: number;
  completFt: number;
  jobId: string;
}

export interface IConsumptionHistory {
  id: string;
  avgFPM: number;
  coilMaterialCode: string;
  coilSerialNumber: string;
  exemptMinutes: number;
  goodFeet: number;
  goodPieceCount: number;
  machineNumber: number;
  materialCode: string;
  nonExemptMinutes: number;
  ordId: number;
  orderCode: number;
  productionDate: Date;
  reclaimedScrapFeet: number;
  recordCount: number;
  runMinutes: number;
  scrapFeet: number;
  scrapPct: number;
  scrapPieceCount: number;
  targetFPM: number;
  toolingCode: string;
  totalFeet: number;
  totalMinutes: number;
  totalOperationMinutes: number;
}

export interface IConsumptionHistoryMachine extends IConsumptionHistory {
  machine?: IMachine;
}

export interface ITask {
  id: string;
  taskType: string;
  coilType: ICoilType;
  requiredFt: number;
  sourceLocationId: string;
  destinationLocationId: string;
  requiredDate: string;
  startedDate: Date;
  availableDate: Date;
  completedDate: Date;
  taskState: string;
  preferredCoils: ITaskCoil[];
  nonpreferredCoils: ITaskCoil[];
  selectedCoilId?: string;
  userName: string;
  overrideCode: IReasonCode;
  sourceLocation?: string;
  desinationLocation?: string;
}

export interface ITaskCoil extends ICoil {
  id: string;
  warningFields: string[];
}

export interface IReasonCode {
  id: string;
  codeSet: string;
  reason: string;
  name?: string;
}

export enum LocationCategory {
  Machine = 1,
  Warehouse,
  StagingBay,
  LoadingDock,
  Bin,
}
export interface ILocation {
  id: string;
  code: string;
  name: string;
  category: LocationCategory;
}

export interface IWarehouseLocation {
  title: string;
  items: ILocation[];
  doNotEdit?: boolean;
  category: string;
}

export interface ISystemPreferencesService {
  systemPreferences: ISystemPreferences;
}

/**
 * Comparison operators for a {@link ColorRule}. v1 exposes `equals`, `in` and
 * `between` in the editor; the remaining operators are supported by the
 * evaluator so they can be surfaced later without a data-model change.
 */
export type ColorRuleOperator =
  | 'equals'
  | 'notEquals'
  | 'lessThan'
  | 'lessThanOrEqual'
  | 'greaterThan'
  | 'greaterThanOrEqual'
  | 'between'
  | 'in'
  | 'contains';

/**
 * A system-level rule that colors grid cells based on the value of a field.
 * See `specs/color-rules.md`. Applied by the shared `evaluateColor` helper
 * across the scheduler grids and the orders list.
 */
export interface ColorRule {
  id: string;
  enabled: boolean;
  /** Lower is evaluated first; the first matching rule wins. */
  order: number;
  /** Canonical field name the condition tests, e.g. `materialGauge`. */
  sourceField: string;
  operator: ColorRuleOperator;
  /** Primary comparison value. */
  value?: string | number;
  /** Upper bound for `between`. */
  value2?: string | number;
  /** Candidate set for `in`. */
  values?: (string | number)[];
  /** Background color to apply (hex or rgb()). */
  color: string;
  /** Canonical field names this rule paints. */
  targetFields: string[];
}
export interface ISystemPreferences {
  systemLanguage: string;
  inchesUnit: string;
  allowPrereleaseVersions: boolean;
  intranetUrl: string;
  redirectFromLocalhost: boolean;
  allowGuestUser: boolean;
  plantName: string;
  showMaterialShortageAlerts: boolean;
  /** Show XL controller task errors as user-visible alerts. Notification targets and the
   *  cloud push are unaffected by this preference. */
  showTaskErrorAlerts?: boolean;
  /** Suppress the Agent Offline / Eclipse-Comm offline alerts server-side (devices-only sites
   *  with no Eclipse Classic). Also hides them from alert notifications. */
  hideAgentOfflineAlert?: boolean;
  hideCommOfflineAlert?: boolean;
  /** Suppress the Machine Offline alert server-side (demo/test systems where no XL controller
   *  ever connects). Turned on by the server utility's demo-settings. */
  hideMachineOfflineAlert?: boolean;
  enableMaterialTasks: boolean;
  defaultDelayCode?: number;
  tailoutScrapCode?: number;
  companyName?: string;
  carryOverShift?: boolean;
  restrictEmailDomains?: boolean;
  allowedEmailDomains?: string[];
  serverIpAddress: string;
  /** System-level cell color rules. See `specs/color-rules.md`. */
  colorRules?: ColorRule[];
  mqttConfiguration?: {
    enabled: boolean;
    brokerHost: string;
    brokerPort: number;
    clientId: string;
    username?: string;
    password?: string;
    useTls: boolean;
    cleanSession: boolean;
    keepAliveInterval: number;
    reconnectDelay: number;
    defaultQosLevel: number;
    useRetainedMessages: boolean;
    responseTimeout: number;
  };
  /** Eclipse Classic system settings (table cleanup / controllers / timing), sourced from
   *  xlwin.ini and edited via a dedicated endpoint. */
  classicSettings?: IClassicSystemSettings;
  /** Feature flag for the schedule Optimize button. See `specs/auto-scheduling.md`. */
  enableAutoScheduling?: boolean;
  /**
   * Plant-wide time of day a job is due on its required date, as a .NET TimeSpan string
   * ("HH:mm:ss"). Null/absent = end of day.
   */
  scheduleCutoffTime?: string | null;
  /** Which order date places a job on a truck route instance. */
  truckDateSource?: TruckDateSource;
  /** A job projected to finish within this many minutes of its due time is at risk. */
  truckWarningMinutes?: number;
  /** The app theme for a user who hasn't picked one: 'steel', or anything else for classic.
   *  Set by the supervisor's demo profiles. */
  defaultAppTheme?: string | null;
}
export type TruckDateSource = 'RequiredDate' | 'ShipDate' | 'ShipDateThenRequiredDate';
export type DueTimeSource = 'None' | 'PlantDefault' | 'RouteDefault' | 'DayOfWeek' | 'DateOverride';
export type OrderProductionStatus = 'Unscheduled' | 'Scheduled' | 'Running' | 'Done' | 'OnHold';
export type LatenessLevel = 'OnTime' | 'NotScheduled' | 'AtRisk' | 'Late';
/**
 * One truck route instance (normalized truck + date) on the Trucks page, pushed live as the
 * `TruckInstanceState` collection. See `specs/truck-schedule.md`.
 */
export interface ITruckInstanceState {
  /** The document id, e.g. `TruckInstanceState/ROUTE%205/2026-09-23`. */
  id: string;
  /** Normalized truck number; '' for Unassigned (jobs with no truck). */
  key: string;
  /** Null for Unassigned. */
  displayName: string | null;
  /** The instance date (local midnight); null for Undated. */
  date: string | null;
  dueAt: string | null;
  dueSource: DueTimeSource;
  level: LatenessLevel;
  /** Null while any open job is unscheduled or on hold, or when every job is done. */
  projectedReadyAt: string | null;
  isOpen: boolean;
  unscheduledCount: number;
  scheduledCount: number;
  runningCount: number;
  doneCount: number;
  onHoldCount: number;
  totalFt: number;
  remainingFt: number;
  lateSince: string | null;
  alertActive: boolean;
  jobs: ITruckInstanceJob[];
}
export interface ITruckInstanceJob {
  ordId: number;
  orderCode: string;
  customerName: string;
  materialCode: string;
  toolingCode: string;
  status: OrderProductionStatus;
  level: LatenessLevel;
  projectedCompletion: string | null;
  machineNumber: number | null;
  totalFt: number;
  remainingFt: number;
  lateSince: string | null;
  alertActive: boolean;
}
/** Times are "HH:mm:ss". */
export interface ITruckRoute {
  key: string;
  displayName: string;
  defaultDueTime: string | null;
  /** Seven entries, Sunday first; null inherits the route default. */
  dayOfWeekDueTimes: (string | null)[];
  dateOverrides: { date: string; dueTime: string }[];
  modifiedBy: string;
  modifiedAt: string;
  /** Null when the route has no document yet. Sent back as `expectedChangeVector` on a save. */
  changeVector: string | null;
}
export interface ITruckRoutes {
  /** Null means end of day. */
  plantDefault: string | null;
  routes: ITruckRoute[];
}
export interface IClassicSystemSettings {
  // Table cleanup
  deleteDoneOrders: boolean;
  deleteDoneOrdersAfterDays: number;
  deleteUnusedOrders: boolean;
  deleteUnusedOrdersAfterDays: number;
  deleteIncompleteOrders: boolean;
  deleteIncompleteOrdersAfterDays: number;
  deleteTemporaryParts: boolean;
  deleteTemporaryPartsAfterDays: number;
  deleteUnusedParts: boolean;
  deleteUnusedPartsAfterDays: number;
  deleteShipmentInfo: boolean;
  deleteShipmentInfoAfterDays: number;
  deleteCompleteCoils: boolean;
  deleteCompleteCoilsAfterDays: number;
  deleteUnneededPatternsAfterHours: number;
  // Importing
  /** [XLCommon] UseSegmentImport — segment-transition flag for XLComm pattern import. */
  useSegmentImport: boolean;
  // Controllers
  verifyCoilsWhenLoaded: boolean;
  /**
   * [XLCommon] ProCoilValidation — coil verification is handled by Eclipse Pro's coil validation
   * channel instead of Classic. With verifyCoilsWhenLoaded this forms the three-way verify-coils
   * mode: not enabled / Classic / Pro.
   */
  proCoilValidation: boolean;
  disableCoilOkMessage: boolean;
  enableHoleCounting: boolean;
  disableSpecificOrderRequests: boolean;
  // Timing
  statusCheckSeconds: number;
  controllerClockToleranceSeconds: number;
  packAndReindex: boolean;
  /** Time of day to pack & re-index, as seconds since midnight. */
  packAndReindexAtSecondsSinceMidnight: number;
  /** Read-only diagnostic: when Classic last packed & re-indexed (ISO string), null if never. */
  lastPackDate?: string | null;
}
export interface IPackReindexStatus {
  inProgress: boolean;
  /** Live LastPackDate read from Classic's ini (ISO string), so the UI can refresh on completion. */
  lastPackDate?: string | null;
}
export interface ISystemInfo {
  version: string;
  serverId: string;
  serverStartTime: Date;
  /** Tells you if SignalR is connected to the ClientDataHub. */
  isSignalRConnected: boolean;
  signalRConnectionId: string;
  /** Details of the most recent release, pulled from nuget */
  latestReleaseVersion?: {
    description: string;
    published: string; // Date
    version: string;
  };
}
export interface IPendingActionsToAgent {
  id: string;
  type: string;
  count: number;
}
export interface ILanguage {
  title: string;
  translation: string;
  code: string;
}

export interface IAlert {
  id: string;
  isCritical: boolean;
  alertType: string;
  description?: string;
  icon?: string;
  subTitle?: string;
  title?: string;
  /** What the alert is about (machine, truck, order, pattern, location); unset when the title is. */
  subject?: string;
  iconColor?: string;
  actions?: any[];
  percentComplete?: number;
  kbLink?: string;
  sref?: string;
  created?: string;
  machine?: IMachine;
  expectedDate?: string;
  effectiveDate?: string;
  machineNumber?: number;
  deviceId?: string;
  referenceDocumentId?: string;
  /** Also shown in the banner under the Steel header (dismissable until the next day). */
  showInBanner?: boolean;
}

export interface IAndonSequencePanel {
  viewKey: string;
  duration: string | number;
  playKey: string;
  title?: string;
  chart: boolean;
  options: IAndonSequencePanelOptions;
}

export interface IAndonSequencePanelOptions {
  message?: string;
  shiftRun_ShowThroughput?: boolean;
  shiftRun_ThroughputTimeUnit?: string;
  shiftRun_RunTimeUnit?: string;
  upcomingBundles_ColumnFields?: string[];
  hourlyChart_MetricGroup?: string;
  multi_A_ViewKey?: string;
  multi_A_Options?: IAndonSequencePanelOptions;
  multi_B_ViewKey?: string;
  multi_B_Options?: IAndonSequencePanelOptions;
  multi_C_ViewKey?: string;
  multi_C_Options?: IAndonSequencePanelOptions;
  multi_D_ViewKey?: string;
  multi_D_Options?: IAndonSequencePanelOptions;
}

export interface IAndonSequenceConfig {
  id: string;
  documentID?: string;
  count?: number;
  name?: string;
  panels: IAndonSequencePanel[];
}

export interface IAndonView {
  name: string;
  viewKey: string;
  hasChart: string;
  editKey: string;
}

export type WallboardContentType =
  | 'NoContent'
  | 'Andon'
  | 'DeviceAndon'
  | 'ExternalUrl'
  | 'Message'
  | 'Warehouse'
  | 'ProductionSummary'
  | 'AndonGrid'
  | 'Dashboard'
  | 'Playlist'
  | 'TruckSchedule';

export interface IWallboardDevice {
  id: string;
  wallboardDeviceKey: string;
  wallboardDeviceName: string;
  contentType: WallboardContentType;
  deviceParams: IWallboardDeviceParams;
}

/** A named wallboard: content config decoupled from any device registration. */
export interface IWallboardDefinition {
  id?: string;
  documentID?: string;
  name: string;
  contentType: WallboardContentType;
  deviceParams: IWallboardDeviceParams;
}

export interface IPlaylistSlide {
  definitionId: string;
  durationSec: number;
}

export interface IWallboardDeviceParams {
  // Andon
  machineNumber: number;
  andonSequenceId: string;
  showSchedule: boolean;
  forcePanelIndex: number;

  // DeviceAndon
  deviceId: string;

  // Message
  message: string;

  // ExternalUrl
  externalUrl: string;

  // ProductionSummary / Dashboard
  machines: string[];
  devices: string[];
  metrics: string[];
  ranges: string[];
  cycleSec: number;

  // Dashboard hourly chart
  hourlyChartMetric: string;
  deviceHourlyChartMetric: string;

  // TruckSchedule: comma-separated wildcard patterns (blank = every truck), and the day window.
  // Null days use App.Wallboard's defaults.
  truckFilter?: string;
  truckDaysBefore?: number;
  truckDaysAfter?: number;

  // General
  theme: string;

  // Debug overlay (App.Wallboard troubleshooting)
  debugOverlayEnabled: boolean;

  // Playlist
  slides?: IPlaylistSlide[];
}

export interface ITaskFacet {
  title: string;
  filters: ITaskFacetValue[];
}
export interface ITaskFacetValue {
  id: string;
  title: string;
}
export interface IUserTaskFilter {
  filterId: string;
  checked: boolean;
}

export interface IUserTaskFilters {
  id: string;
  userName: string;
  filters: IUserTaskFilter[];
}
export interface IOrderImportEvent {
  id: string;
  batchId: string;
  settingsId: string;
  start: Date;
  state: string;
  recordCount: number;
  importedCount: number;
  rejectedCount: number;
  rejects: {
    externalSystemRecordId: string;
    recordTitle: string;
    failureMessages: string[];
  }[];
  successUpserts: {
    notificationFailures: string[];
    externalSystemRecordId: string;
    recordTitle: string;
  }[];
}
export type CoilSyncType = 'Import' | 'FullSync' | 'IncrementalSync';

export interface ICoilImportEvent {
  id: string;
  batchId: string;
  settingsId: string;
  start: Date;
  state: string;
  recordCount: number;
  importedCount: number;
  rejectedCount: number;
  syncType: CoilSyncType;
  errors: string[];
  rejects: {
    externalSystemRecordId: string;
    failureMessages: string[];
  }[];
}
export interface IIntegrationConfig {
  id: string;
  type: string;
  state: string;
  enabled?: boolean;
  settings: any;
  messages: string[];
}
export interface IOrderImportConfig extends IIntegrationConfig {}
export interface ICoilImportConfig extends IIntegrationConfig {}
export interface IExportConfig extends IIntegrationConfig {}

export interface IExportEvent {
  id: string;
  channel: string;
  itemId: string;
  complete: boolean;
  stage: string;
  receivedTime: Date;
  activityLog: string[];
  executionDuration: TimeSpan;
  attemptCount: number;
  relatedId: string;
  relatedDesc: string;
}
export interface IMaterialImportConfig extends IIntegrationConfig {}
export interface IMaterialImportEvent {
  id: string;
  batchId: string;
  settingsId: string;
  start: Date;
  state: string;
  errors: string[];
  recordCount: number;
  importedCount: number;
  rejectedCount: number;
  rejects: {
    externalSystemRecordId: string;
    failureMessages: string[];
  }[];
}
export interface IScheduleSyncConfig extends IIntegrationConfig {}
export interface IScheduleSyncEvent {
  id: string;
  batchId: string;
  settingsId: string;
  start: Date;
  state: string;
  recordCount: number;
  importedCount: number;
  rejectedCount: number;
  rejects: {
    externalSystemRecordId: string;
    failureMessages: string[];
  }[];
}

// CMMS Export
export type CmmsUnitConversion =
  | 'None'
  | 'MinutesToHours'
  | 'HoursToMinutes'
  | 'SecondsToHours'
  | 'FeetToMeters'
  | 'MetersToFeet'
  | 'Multiplier'
  | 'Divisor';

export interface ICmmsLogTypeMapping {
  logType: string;
  statisticField: string;
  conversion: CmmsUnitConversion;
  factor?: number; // Used with Multiplier or Divisor conversions
}

export interface ICmmsLogProfile {
  profileName: string;
  machineType: string; // 'Device' or 'XLMachine' - determines available statistics fields
  logTypeMappings: ICmmsLogTypeMapping[];
}

/** One machine and the CMMS assets it posts to (the machine and its sub-assets). A machine is listed once. */
export interface ICmmsMachineMapping {
  machineType: string; // 'Device' or 'XLMachine'
  machineId: string;
  startDate: string | Date; // ISO string from server, Date in UI
  assets: ICmmsAssetMapping[];
  tools?: ICmmsToolMapping[]; // XL machines: tool numbers posted to their own asset
}

export interface ICmmsAssetMapping {
  externalAssetId: string; // Manager Plus asset ID or Fiix asset code
  logProfileName: string;
  startDate?: string | Date; // only where it differs from the machine's (kept from older configs)
}

/** One tool number of an XL machine and the CMMS asset (a Fiix sub-asset) its punch hits are posted to. */
export interface ICmmsToolMapping {
  toolId: number; // the controller's tool number
  externalAssetId: string;
  unit: string; // meter unit (log type), e.g. 'Cycles'
  startDate?: string | Date; // only where it differs from the machine's
}

/** A tool number from a machine's production, with its hits (POST cmmsExportConfigs/tool-hits). */
export interface ICmmsToolHits {
  toolId: number;
  hits: number; // since the tool's or machine's start date
  pendingHits?: number; // mapped tools: not posted yet
  incompleteEvents: number; // events whose tool list may be cut short
  lastHit: string;
}

/** Which asset codes the CMMS has (POST cmmsExportConfigs/{id}/check-assets). */
export interface ICmmsAssetCheck {
  checked: boolean; // false when the provider can't look assets up
  found: { [code: string]: string }; // code -> asset name
  missing: string[];
  ambiguous: string[];
  error?: string;
}

export interface ICmmsExportConfigStatus {
  isRunning: boolean;
  isProcessing: boolean;
  lastRunTime?: Date;
  totalLogsPublished: number;
  failedAttempts: number;
  nextAttemptUtc?: Date; // set while backing off after failed attempts
  callsToday?: number; // CMMS API calls this UTC day
  dailyCallBudget?: number; // Fiix only; null when the provider has no daily limit
  historyCallsToday?: number; // history API calls this UTC day, not included in callsToday
  historyCallBudget?: number; // Fiix only; null when the provider has no daily history limit
  historyThrough?: Date; // while catching up on history: how far the furthest-behind mapping has posted
  recentMessages: string[];
  processedMachines: { machineKey: string; lastProcessedPeriod: Date }[];
}

export interface ICmmsConnectionTestResult {
  success: boolean;
  messages: string[];
  warnings: string[]; // config problems, one line each (covers the lists below)
  missingAssets: string[];
  ambiguousAssets: string[];
  missingUnits: string[];
  duplicateMeters: string[];
  missingToolAssets?: string[]; // tool hits wait until these exist
}

export interface ICmmsExportConfig {
  id: string;
  enabled: boolean;
  implementation: string;
  externalConnectionId: string;
  pollIntervalMinutes: number;
  triggerType: 'Hourly' | 'Shift'; // Determines data source: Hourly or Shift/Daily
  logProfiles: ICmmsLogProfile[];
  machineMappings: ICmmsMachineMapping[];
  fiix?: ICmmsFiixSettings; // Fiix configs only
  status?: ICmmsExportConfigStatus;
}

export interface ICmmsFiixSettings {
  site: string; // Fiix site name or code; scopes asset-code lookups
  maxBatchSize: number;
  dailyCallBudget: number;
  historyCallBudget: number; // calls per UTC day for periods more than 48 h old (the catch-up)
}

export interface IBundleResult {
  id: string;
  bundleCode: string;
  ordId: number;
  orderCode: string;
  materialCode: string;
  toolingCode: string;
  workOrder: string;
  bundleNumber: number;
  producedLengthIn: number;
  longestLengthIn: number;
  exportComplete: boolean;
  hide: boolean;
  complete: boolean;
}

export interface IRecentBundleResult {
  id: string;
  bundleCode: string;
  ordId: number;
  orderCode: string;
  materialCode: string;
  toolingCode: string;
  workOrder: string;
  bundleNumber: number;
  producedLengthIn: number;
  longestLengthIn: number;
  exportComplete: boolean;
  hide: boolean;
  complete: boolean;
  isTagPrinted: boolean;
}

export type Percentage = number;
export type Length = number;
export type Cardinal = number;
export type Velocity = number;
export type TimeSpan = number;
export type UnitOfMeasure = Percentage | Length | Cardinal | Velocity;

export interface IBulletChartModel<TUnitOfMeasure> {
  targetValue: TUnitOfMeasure;
  okRangeStart: TUnitOfMeasure;
  okRangeEnd: TUnitOfMeasure;
  maxValue: TUnitOfMeasure;
  minValue: TUnitOfMeasure;
  // The actual current value
  value: TUnitOfMeasure;
}

export interface IHistoricValue<TUnitOfMeasure> {
  shiftCode: string;
  value: TUnitOfMeasure;
}

export interface IOneValueModel<T> {
  // The primary value of the metric.
  primary: T;
  // The sparkline values
  history: IHistoricValue<T>[];
  // The bullet chart data points
  bullet: IBulletChartModel<T>;
}
export interface ITwoValuesModel<TPrimary, TSecondary> extends IOneValueModel<TPrimary> {
  // The secondary value of the metric.
  secondary: TSecondary;
}

export interface IProductionSummaryReportRecord {
  id: number;
  totalGoodLn: ITwoValuesModel<Length, Cardinal>;
  netScrap: ITwoValuesModel<Percentage, Length>;
  runningThroughput: IOneValueModel<Velocity>;
  oee: IOneValueModel<Percentage>;
  target: IOneValueModel<Percentage>;
  availability: IOneValueModel<Percentage>;
  speed: IOneValueModel<Percentage>;
  yield: IOneValueModel<Percentage>;
  timeBar: {
    running: Percentage;
    changeover: Percentage;
    breakdown: Percentage;
    otherUnexpected: Percentage;
    otherExpected: Percentage;
    unscheduled: Percentage;
    availableTime: TimeSpan;
    durationTime: TimeSpan;
  };
}

export interface IMaterialUsageRecord {
  lengthScrapFt: number;
  lengthUsedFt: number;
  materialCode: string;
  scrapPerc: number;
}

export interface IMaterialUsageReportRecord {
  duration: string;
  key: { day: number; month: number; year: number };
  lengthScrapFt: number;
  lengthUsedFt: number;
  scrapPerc: number;
  records: IMaterialUsageRecord[];
}

export interface IProductionSummaryReportModel {
  records: IProductionSummaryReportRecord[];
}

export interface IBundlingRule {
  maxPieceCount: number;
  maxWeightLbs: number;
  minPctOfMaxLength: Percentage;
  itemSort: string;
}

export interface IBundlingRulesDocument {
  systemLevel: IBundlingRule;
  customerRules: { [key: string]: IBundlingRule };
  toolingDefRules: { [key: string]: IBundlingRule };
  materialToolingRules: ({ material: string; tooling: string } | IBundlingRule)[];
  customerToolingRules: ({ customer: string; tooling: string } | IBundlingRule)[];
}

/// The levels a bundling rule can be defined at, most general first. The order
/// matters: itemSort comes from the last level that supplies one.
export type BundlerRuleLevel =
  | 'System'
  | 'Order'
  | 'Tooling'
  | 'Customer'
  | 'MaterialTooling'
  | 'CustomerTooling';

/// One level's contribution to a resolved rule set. Levels that matched nothing
/// are included too, so the tester can show what was looked for and came up empty.
export interface IBundlingRuleLayer {
  level: BundlerRuleLevel;
  lookedFor: string;
  matchedKey: string | null;
  matched: boolean;
  matchedLoosely: boolean;
  maxWeightLbs: number | null;
  maxPieceCount: number | null;
  minPctOfMaxLength: number | null;
  itemSort: string | null;
}

/// Describes where the values fed to the bundler rules come from for one order import.
export interface IBundlingRuleInputSource {
  customerFrom: string;
  toolingFrom: string;
  materialFrom: string;
  note: string | null;
}

export interface IBundlingRuleTestResult {
  input: {
    customer: string;
    tooling: string;
    material: string;
    orderMaxWeightLbs: number | null;
    orderMaxPieceCount: number | null;
  };
  rules: IBundlingRule;
  sources: {
    maxWeightLbs: BundlerRuleLevel | null;
    maxPieceCount: BundlerRuleLevel | null;
    minPctOfMaxLength: BundlerRuleLevel | null;
    itemSort: BundlerRuleLevel | null;
  };
  layers: IBundlingRuleLayer[];
  warnings: string[];
  toolingSources: {
    imports: {
      name: string;
      implementation: string;
      enabled: boolean;
      bundleByRules: boolean;
      source: IBundlingRuleInputSource;
    }[];
    rebundleFromSchedule: IBundlingRuleInputSource;
  };
}

export interface IPrintTemplate {
  id: string;
  name: string;
  pageHeightIn: number;
  pageWidthIn: number;
  type: string;
}

/// Bundle and coil tag printing configuration.
export interface IBundlePrintConfig {
  bundleTagEnabled: boolean;
  bundleTagProperties: Object;
  bundleTagStartingDate: Date;
  isConfigured: boolean;
  messages: string[];
  source?: any;
}

export interface ICoilPrintConfig {
  coilTagEnabled: boolean;
  coilTagProperties: Object;
  coilTagStartingDate: Date;
  isConfigured: boolean;
  messages: string[];
  defaultTemplate: string;
}

export interface IMachinePrintConfig {
  id: number;
  machineNumber: number;
  bundlePrinterName: string;
  defaultBundleTemplate: string;
  bundlePrintCount: number;
  coilTagTemplate: string;
  coilTagPrinterName: string;
  coilTagPrintCount: number;
}

export interface IInstalledPrinters {
  printers: IPrinterDetail[];
}

export interface IPrinterDetail {
  id: string;
  name: string;
  fullName: string;
  description: string;
  //// theses are currently commented out in the .cs
  // location: string;
  // hasPaperProblem: boolean;
  // isInError: boolean;
  // isOffline: boolean;
  // isPaused: boolean;
  // numberOfJobs: number;
  // hostingPrintServer: string;
  // shareName: string;
  // queueStatus: string;
  // queuePortName: string;
  // queueDriverName: string;
}

export interface IHealth {
  id: string;
  effectsName: string;
  serviceName: string;
  status: 'Starting' | 'Started' | 'HadError' | 'FatalError' | 'NormalShutdown';
  error: string[]; // Exception
}

export type HealthSummaryType = {
  collection: string;
  count: number;
};

export interface ISyncState {
  id: string;
  changeId: number;
  updatesCount: number;
  deletesCount: number;
  startTime: Date;
  lastSyncTime: Date;
  updatesPerMin: number;
  deletesPerMin: number;
}

export interface IWorkloadHealthCheck {
  isHealthy: boolean;
  checkTime: Date;
  responseTimeMs: number;
  errorMessage?: string;
  healthStatus: string;
}

export interface IWorkloadStatus {
  id: string;
  name: string;
  description: string;
  enabled: boolean;
  status: string;
  processId?: number;
  startTime?: Date;
  stopTime?: Date;
  lastError?: string;
  version?: string;
  channel: string;
  lastHealthCheck?: IWorkloadHealthCheck;
  restartAttempts: number;
  dependencies: string[];
}

export interface IWorkloadUpdateDto {
  workloadId: string;
  currentVersion: string;
  availableVersion: string;
  channel: string;
  releaseNotes: string;
}

export interface ISupervisorInstallerInfo {
  url: string;
  version: string;
}

export interface IUserSession {
  claims: string[];
  roles: string[];
  userName: string;
  token: string;
  isNavigationFolded: boolean;
  serverId: string;
  // Effective values: the user's override if set, otherwise the system default.
  language: string;
  inchesUnit: string;
  // Raw overrides: null/empty when the user is using the system default.
  userLanguage?: string;
  userInchesUnit?: string;
}

export interface IUser {
  id: string;
  userName: string;
  fullName: string;
  email: string;
  roles: string[];
  folderRoles: {
    folderId: string;
    role: 'None' | 'Operator' | 'Administrator';
  }[];
  folderPin: string;
  firstName: string;
  lastName: string;
  // Per-user overrides; null/empty means "use system default".
  language?: string;
  inchesUnit?: string;
  // Set when this user is promoted from / linked to a Classic Employee (XL operator).
  employeeId?: number;
}

export interface IEmployee {
  employId: number;
  name: string;
  changeId: number;
  isPromoted: boolean;
  linkedUser: IUser | null;
}

export type IPathfinderMachine = {
  id: string;
  serialNumber: string;
  name: string;
  accepted: boolean;
};

export interface IUnitDef {
  key: string;
  system: string;
  base: string;
  title: string;
}

export type ExpressCommState = {
  id: string;
  connectionState?: string;
};
export type ExpressCtrlState = {
  id: string;
  machineNumber: number;
  address: string;
  connectionState: string;
  last: {
    command: string;
    response: string;
    responseReceived: string; // a date
  };
  txCount: number;
  sparkData: ISparkData[];
};

export interface ISparkData {
  ordinal: number;
  value: number;
}

/** One raw traced frame from the ExpressProxy. See TrafficDirection/TrafficFlags server-side. */
export type CommTraceEvent = {
  seq: number;
  t: string; // timestamp
  dir: number; // 0 E->P, 1 P->C, 2 C->P, 3 P->E
  flags: number; // 1 synthesized, 2 ping, 4 bad frame, 8 dropped, 16 connect, 32 disconnect
  unit: string;
  data: string; // base64 raw frame bytes
};

/**
 * Singleton collection: each push replaces the previous batch; the UI keeps its
 * own rolling buffer merged by event seq.
 */
export type CommTraceBatch = {
  id: string;
  seed: boolean;
  droppedCount: number;
  events: CommTraceEvent[];
};

/** We've somewhat standardized on this shape when the api returns 400 (bad result). */
export type BadRequestResponse = {
  errors: string[];
};

export interface IProductionEvent {
  id: string;
  type: string;
  reason: string;
  eventTitle: string; // or enum
  recordDate: Date;
  productionDate: Date;
  shiftCode: string;
  shift: number;
  machineNumber: number;
  machineDescription: string;
  orderCode: string;
  materialCode: string;
  toolingCode: string;
  ordId: number;
  itmId: number;
  bundle: number;
  partLengthIn: number;
  quantity: number;
  producedLengthFt: number;
  consumedLengthFt: number;
  goodFt: number;
  scrapFt: number;
  exemptScrapFt: number;
  reclaimedScrapFt: number;
  coilSerialNumber: string;
  coilMaterialCode: string;
  durationMinutes: number;
  runMinutes: number;
  downMinutes: number;
  exemptMinutes: number;
  lossCode: number;
  lossDescription: string;
}

export interface IDeviceProductionEvent {
  id: string;
  deviceId: string;
  partId: string;
  partName: string;
  jobReference?: string;
  operator: string;
  runState: 'Running' | 'NotRunning';
  material: string;
  cycles: number;
  goodParts: number;
  badParts: number;
  duration: string;
  durationMinutes: number;
  start: Date;
  end: Date;
  shiftCode: string;
  geometry: string;
  edgeId: string;
  blankWidthIn: number;
  blankLengthIn: number;
  expectedCycles?: number;
  delayCode?: number;
  delayCodeExempt?: boolean;
  delayCodeDescription?: string;
  delayCodeGroup?: string;
  downtimeNotes?: string;
  eventTitle: string;
}

export namespace Fx {
  export const ALL = 'ALL';
  export const IN = 'IN';
  export const RANGE = 'RANGE';
  export const ALLRECENT = 'ALLRECENT';

  export class In {
    public type = IN;
    constructor(
      public property: string,
      public values: (string | number)[],
    ) {
      if (values.length === 0) throw '`values` array cannot be empty';
    }
  }
  export class All {
    public type = ALL;
    constructor() {}
  }
  export class Range {
    public type = RANGE;
    constructor(
      public property: string,
      public start: any,
      public end: any,
    ) {}
  }
  export class AllRecent {
    public type = ALLRECENT;
    constructor(public daysOld: number) {}
  }

  export type FilterDef = In | All | Range;
  export function toFilterExpr<T>(filterDef: FilterDef): (T) => boolean {
    switch (filterDef.type) {
      case ALL: {
        return (_) => true;
      }
      case IN: {
        const init = (t: T) => false;
        let in_filter = filterDef as In;
        return in_filter.values.reduce(
          (acc, value) => (t: T) => t[in_filter.property] === value || acc(t),
          init,
        );
      }
      case ALLRECENT: {
        // Not exactly accurate, but probably adequate as ALLRECENT is used in
        // only one place at the moment.
        return (_) => true;
      }
      default: {
        throw `FilterDef type '${filterDef.type}' is not implemented`;
      }
    }
  }
  export class Subscription {
    public id: string;

    constructor(
      public collection: string,
      public filterDef: Fx.FilterDef,
    ) {
      this.id = this.uuidv4();
    }

    private uuidv4() {
      // from https://stackoverflow.com/a/2117523/947
      return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        let r = (Math.random() * 16) | 0;
        let v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
      });
    }
  }
}

export type RebundleResult = {
  ordId: number;
  lbsPerFt: number;
  rebundledItems: RebundledItem[];
  rebundledBundles: RebundledBundle[];
};

export type RebundledItem = {
  itemId: number;
  prototypeItemId: number;
  lengthIn: number;
  quantity: number;
  sequence: number;
  bundle: number;
};
export type RebundledBundle = {
  prototypeBundleId: number;
  bundle: number;
};

export type ISingleOrderState = {
  ordId: number;
  clearRebundleOnNextPut: boolean;
  rebundleResult: RebundleResult;
} | null;

export interface IPunchPattern {
  id: string;
  isMacro: boolean;
  isPermanent: boolean;
  patternName: string;
  patternNumber: number;
  punchCount: number;
  importDate: Date;
  lastUsedDate: Date;
}

export interface WarehouseViewModel {
  id: string;
  readyTasks: IWarehouseTask[];
  activeTasks: IWarehouseTask[];
  completeTasks: IWarehouseTask[];
}
export interface IWarehouseTask {
  id: string;
  taskType: string;
  coilType: ICoilType;
  requiredFt: number;
  sourceLocation: ILocation;
  destinationLocation: ILocation;
  requiredDate: string;
  startedDate: Date;
  availableDate: Date;
  completedDate: Date;
  taskState: string;
  preferredCoils: ITaskCoil[];
  nonpreferredCoils: ITaskCoil[];
  userName: string;
  overrideCode: IReasonCode;
  unnattainableCode?: string;
}

export interface ISchedulerGroupSummary {
  count: number;
  totalFt: number;
  //remainingFt: number;
  //estimatedTime: ???;
}

export interface IMaterialColor {
  color: string;
}

export interface IMaterialType {
  type: string;
}

export interface IfpmPlan {
  fpm: number;
  lengthIn: number;
}

export interface IPerformanceDataTool {
  bundleChangePlan: number;
  bundleChangeTarget: number;
  coilChangePlan: number;
  coilChangeTarget: number;
  overrideMachine: boolean;
  toolChangePlan: number;
  toolChangeTarget: number;
  toolingCode: string;
  toolingId: number;
  fpmPlan: IfpmPlan[];
  fpmTarget: IfpmPlan[];
  warningFields: any[];
}

export interface IPerformanceDataDefault {
  bundleChangePlan: number;
  bundleChangeTarget: number;
  coilChangePlan: number;
  coilChangeTarget: number;
  fpmTarget: number;
  overrideMachine?: boolean;
  toolChangePlan: number;
  toolChangeTarget: number;
  fpmPlan: IfpmPlan[];
  warningFields: any[];
}

export interface IPerformanceData {
  description: string;
  id: number;
  machineNumber: number;
  default: IPerformanceDataDefault;
  toolings: IPerformanceDataTool[];
}

/** A single operator's aggregated FPM performance within the history range (top 5 by run hours). */
export interface IOperatorFpm {
  employeeName: string;
  avgFpm: number;
  runHours: number;
}

/** Aggregated FPM performance for a specific part length within the history range. */
export interface ILengthFpm {
  lengthIn: number;
  avgFpm: number;
  runHours: number;
}

/** Run-time-weighted FPM distribution statistics from `/_api/machinePerformanceStandards/history`. */
export interface IFpmHistoryStats {
  medianFpm: number;
  p90Fpm: number;
  weightedAvgFpm: number;
  runHours: number;
  totalGoodFt: number;
  sampleDays: number;
  byOperator: IOperatorFpm[];
  byLength: ILengthFpm[];
}

/** Historical performance for a machine (default) or a tooling on a machine, over the selected date range. */
export interface IPerformanceHistoryStats {
  /** null when there's insufficient data for a meaningful FPM distribution. */
  fpm: IFpmHistoryStats | null;
  coilChangeCount: number;
  toolChangeCount: number;
}

export interface IToolingHistory extends IPerformanceHistoryStats {
  toolingCode: string;
}

/** Response shape of `GET /_api/machinePerformanceStandards/history`. */
export interface IPerformanceHistory {
  machineNumber: number;
  default: IPerformanceHistoryStats;
  toolings: IToolingHistory[];
}

export interface IVersionInfo {
  documentID: string;
  description: string;
  version: string;
  published: string;
}

export interface IUpdateInfo {
  installedVersion: IVersionInfo;
  latestReleaseVersion: IVersionInfo;
}

export interface IReleaseNotesResponse {
  model: string;
  releases: IReleaseNoteEntry[];
}

export interface IReleaseNoteEntry {
  releaseDate: string;
  softwareVersion: string;
  releaseNotesMarkdown: string;
}

export interface IDeviceSystemInfo {
  deviceId: string;
  systemInfo: { [key: string]: string };
  timestamp: string;
}

export interface IAgentStatus {
  rootUrl: string;
  status: {
    documentID: string;
    agentId: string;
    agentVersion: string;
    eclipseDirectory: string;
    eclipseProServerUrl: string;
    eclipseDirectoryValidationErrors: string[];
    eclipseProServerUrlIsGood: boolean;
    eclipseLegacyQueueName: string;
    eclipseLegacyQueueNameIsRunning: boolean;
    errorMessage: string;
    eclipseSchemaVersion: string;
    eclipseSchemaVersionValidationErrors: string[];
    connectedToServer: boolean;
    msmqConfigurationErrors: string[];
    enableProSet: boolean;
    expressPort: number;
    proCoilValidation: boolean;
    classicSerialNumber: number;
    classicMultiUserCode: number;
    agentPlantName: string;
    agentPlantCode: string;
    eclipseVersion: string;
    commIsRunning: boolean;
    importDirect: boolean;
    noMatchParts: boolean;
  };
  isAgentReachable: boolean;
}

export interface ApiErrorResult {
  errors: string[];
}

export interface IFeatureFlags {}
export interface TransParams {
  routerId: string;
  queryParams: Object;
}

export interface IDelayScrapCode {
  category: string;
  code: number;
  description: string;

  exempt: boolean;

  group: string;
  historic: boolean;
  id: string;
  loggedProduction: number;
  responsibility: string;
  workGroup: string;

  /** NetSuite account number for this scrap type (e.g., "5900") */
  externalAccount?: string;
  /** NetSuite Adjustment Reason Code internal ID for custbody27 */
  externalCode?: string;
}

export interface ICategory {
  translateKey: string;
  value: string;
}
export type IStrategy =
  | 'ByBundleNumAscThenLengthDesc'
  | 'ByBundleNumAscThenLengthAsc'
  | 'ByBundleNum'
  | 'BundlesByLengthDesc'
  | 'BundlesByLengthAsc';

export interface ISyncAlertCollection {
  totalCount: number;
  missingIds: string[];
  lastChecked: string;
}

export interface IDataSyncAlert {
  inRavenButNotDbf: { [key: string]: ISyncAlertCollection };
  inDbfButNotRaven: { [key: string]: ISyncAlertCollection };
  lastUpdated: string;
}

export interface IAliasEntries {
  aliasName: string;
  shapeName: string;
  xOffset: number;
  yOffset: number;
}

export interface IShapeAliasTable {
  aliasEntries: IAliasEntries[];
}

export type ShapeGeometryKind = 'Round' | 'RoundedSlot' | 'Square' | 'SquareSlot' | 'Triangle';
export type TriangleDirection = 'Positive' | 'Negative';

/**
 * Drawn-hole geometry for one MACHINE shape code (never an alias name — aliases resolve first).
 * Drawing-only: the solve never reads this. Single-dimension kinds (Round, Square) carry yIn = 0.
 */
export interface IShapeGeometryEntry {
  shapeCode: string;
  kind: ShapeGeometryKind;
  /** Extent along the strip (part X), inches. Round: diameter. Square: side. Triangle: base. */
  xIn: number;
  /** Extent across the strip (part Y), inches. Slots: Y extent. Triangle: height. */
  yIn: number;
  /** Triangle only: apex direction in strip Y, as the die is mounted (pre shape-option flips). */
  direction: TriangleDirection;
  description: string | null;
}

export interface IShapeGeometryTable {
  entries: IShapeGeometryEntry[];
}

/** Per-machine mapping of vendor segment names to canonical part-frame segments (SegmentAliasTable doc). */
export interface ISegmentAliasTable {
  machineNumber: number;
  aliasEntries: ISegmentAliasEntry[];
}

export interface ISegmentAliasEntry {
  /** As sent by the external system, e.g. "Flange Left". */
  aliasName: string;
  /** Canonical: 'Web' | 'FlangePlus' | 'FlangeMinus' (Phase 2: any profile segment name). */
  segmentName: string;
  /** Anchor the vendor measures from when the punch's y-ref is None; 'None' = the segment's own default. */
  defaultReference: 'None' | 'CenterPlus' | 'CenterMinus' | 'PlusEdge' | 'MinusEdge';
  /** Which tooling column holds this flange's height; 'Default' = the canonical side's column. Ignored on Web. */
  legHeightColumn: 'Default' | 'LegHeight' | 'LegHeight2';
}

/**
 * Per-machine geometry corrections for segment resolution — the v2, direction-keyed model
 * (SegmentGeometryTable doc): per-tooling direction rules join machine-wide correction cells
 * keyed (gauge, side, direction). Directions are load-bearing: a wrong rule can move a hole,
 * and the solve fails loud (SegmentCorrectionNotFound) when a commissioned combination is
 * unresolvable.
 */
export interface ISegmentGeometryTable {
  machineNumber: number;
  directionRules: ISegmentDirectionRule[];
  corrections: IBendCorrectionCell[];
}

/**
 * Per-machine table of permanent patterns appended after the solution, selected by the job's
 * tooling code (ToolingCodePatternTable doc). The sibling of part printing, which keys off part
 * length instead. Tool/macro punches only; they land after the solved shape operations, so a rule
 * starting or stopping to match never renumbers the operations already there.
 */
export interface IToolingCodePatternTable {
  machineNumber: number;
  rules: IToolingCodePatternRule[];
}

export interface IToolingCodePatternRule {
  /** Wildcard matched against the job's tooling code; the single most specific match wins. */
  toolingCodePattern: string;
  /** Document id of a permanent, non-macro punch pattern. */
  patternId: string;
  description: string;
}

export interface ISegmentDirectionRule {
  /** Wildcard pattern matched against the job's tooling code; cascade — most specific non-null value per field wins. */
  toolingCodePattern: string;
  /** Which way each flange bends, machine-run (post-flip) frame — "as the part exits". Null = inherit from a broader rule. */
  flangeDirectionPlus: BendDirection | null;
  flangeDirectionMinus: BendDirection | null;
  /** Rare measured web-center offset override, inches; null = derive from the leg heights. */
  webCenterOffset: number | null;
}

/** One commissioned correction cell — machine-wide, no tooling pattern in the key. */
export interface IBendCorrectionCell {
  /** Gauge this cell applies to (exact match); null = every gauge (fallback). */
  gauge: number | null;
  side: 'FlangePlus' | 'FlangeMinus';
  direction: BendDirection;
  /** Signed formed-to-flat correction per web/flange bend, inches. */
  bendCorrection: number;
}

/** Out-of-strip-plane flange direction as the part exits the machine. */
export type BendDirection = 'Up' | 'Down';

/** Row of GET _api/tooling (ToolingMaps.ToWebListDto). */
export interface IToolingListItem {
  id: string;
  toolingCode: string;
  description: string;
  pCodeGroup: string;
  machines: { machineNumber: number; description: string }[];
  hasUnassignedMachineTool: boolean;
  /** Distinct machine values, joined — a comma-separated list means the tools disagree. */
  finWidth: string;
  legHeight: string;
  legHeight2: string;
}

/** Commissioning worksheet (POST machine/{id}/segmentworksheet): fit geometry corrections from known-good machine positions. */
export interface ISegmentWorksheetArg {
  toolingCode: string;
  /** Gauge the known-good rows were run at; unset targets a gauge-agnostic entry. */
  gauge?: number | null;
  rows: {
    segment: string;
    segmentOffset: number;
    /** Position as shown in the controller pattern; null = predict only. */
    xlPosition: number | null;
  }[];
}

export interface ISegmentWorksheetResult {
  machineNumber: number;
  toolingCode: string;
  gauge: number | null;
  flipY: boolean;
  optionYOffset: number;
  stripOffset: number;
  finWidth: number;
  /** The values the solve would use today (v2 lookup result), incl. resolution errors. */
  existing: {
    correctionPlus: number;
    correctionMinus: number;
    webCenterOffset: number | null;
    directionPlus: BendDirection | null;
    directionMinus: BendDirection | null;
    cellGaugePlus: number | null;
    cellGaugeMinus: number | null;
    errors: string[];
  } | null;
  /** The tooling's resolved directions — Apply routes each side's value through these. */
  directions: {
    plus: BendDirection | null;
    plusRule: string | null;
    minus: BendDirection | null;
    minusRule: string | null;
  } | null;
  /** The leg-height-derived Δ, for reference alongside the fitted value. */
  derivedWebCenterOffset: number;
  suggestion: {
    /** Value for the plus side's (gauge, side, direction) cell; null = undetermined, Apply leaves the cell alone. */
    bendCorrectionPlus: number | null;
    bendCorrectionMinus: number | null;
    /** The fitted Δ (absolute), for display. */
    webCenterOffset: number | null;
    /** Non-null = Apply writes this onto an exact-pattern direction rule; null = effective Δ already matches. */
    webCenterOffsetOverride: number | null;
    message: string | null;
  };
  rows: {
    segment: string;
    canonicalSegment: string | null;
    anchor: string | null;
    segmentOffset: number;
    xlPosition: number | null;
    currentPredictedXl: number | null;
    suggestedPredictedXl: number | null;
    error: string | null;
  }[];
}

export interface IMachineMessageLog {
  date: Date;
  message: string;
  changeId: number;
  type: string;
  source: string;
  requestId: string;
  machineNumber: number;
}

export interface IMachineMessageRequest {
  message: string;
  machineNumbers: number[];
  type: MessageDialogType;
  source: string;
  requestId: string;
  color: MessageDialogColor;
}

export enum MessageDialogColor {
  Default = 0,
  Red = 1,
  Yellow = 2,
  Green = 3,
  Black = 4,
  Blue = 5,
}

export enum MessageDialogType {
  Message = 0,
  Warning = 1,
  Error = 2,
}

// External Services Connectivity
export type ExternalServiceHealth = 'Healthy' | 'Unhealthy' | 'Unknown';

export interface IExternalServiceStatus {
  serviceId: string;
  serviceName: string;
  description: string;
  url: string;
  status: ExternalServiceHealth;
  responseTimeMs?: number;
  lastChecked: Date;
  errorMessage?: string;
  metadata?: { [key: string]: any };
}

export interface IExternalServicesReport {
  generatedAt: Date;
  isCached: boolean;
  services: IExternalServiceStatus[];
}

export interface IRavenDbIndexStatus {
  name: string;
  state: string;
  isStale: boolean;
  priority: string;
  type: string;
  lockMode: string;
  lastIndexingTime: Date;
}

export interface IRavenDbHealthStatus {
  isHealthy: boolean;
  lastCheckTime: Date;
  lastSuccessTime?: Date;
  lastFailureTime?: Date;
  consecutiveFailures: number;
  lastError?: string;
  erroredIndexes: string[];
  downtimeDuration?: string;
  databaseName?: string;
}

export interface IRavenDbDatabaseStats {
  databaseId: string;
  countOfDocuments: number;
  countOfIndexes: number;
  countOfStaleIndexes: number;
  staleIndexes: string[];
  lastIndexingTime: Date;
  indexes: IRavenDbIndexStatus[];
}

export interface IRavenDbStatus {
  health: IRavenDbHealthStatus;
  database: IRavenDbDatabaseStats | null;
}

// Pro Online export channel
export type ProOnlineState = 'Dormant' | 'Running' | 'Error';

export interface IProOnlineChannelStatus {
  name: string;
  lastSuccessUtc: Date | string | null;
  lastSuccessCount: number | null;
  lastFailureUtc: Date | string | null;
  lastFailureMessage: string | null;
  pausedReason: string | null;
}

export interface IProOnlineStatus {
  state: ProOnlineState;
  reason: string | null;
  sinceUtc: Date | string | null;
  claimPresent: boolean;
  enabled: boolean;
  enabledLastChangedUtc: Date | string | null;
  enabledLastChangedBy: string | null;
  // Operator-requested history re-send (clears the local sync checkpoint; see
  // IProOnlineHistoryResetResult). Pending while requested is newer than honored.
  historyResetRequestedUtc: Date | string | null;
  historyResetRequestedBy: string | null;
  historyResetHonoredUtc: Date | string | null;
  historyResetPending: boolean;
  environment: string | null;
  endpoint: string | null;
  endpointSource: string | null;
  proSerialNumber: string | null;
  eventsPushedSinceStart: number;
  eventsSyncedThroughShiftCode: string | null;
  draining: boolean;
  channels: IProOnlineChannelStatus[];
}

export interface IProOnlineDrainEstimate {
  horizonMonths: number;
  cutoffShiftCode: string | null;
  eventCount: number;
  isStale: boolean;
  estimatedMinutes: number;
}

export interface IProOnlineEnabledResult {
  enabled: boolean;
  lastChangedUtc: Date | string | null;
  lastChangedBy: string | null;
}

export interface IProOnlineHistoryResetResult {
  historyResetRequestedUtc: Date | string | null;
  historyResetRequestedBy: string | null;
  historyResetHonoredUtc: Date | string | null;
  historyResetPending: boolean;
}
